import { useToast } from "@/store";
import { captureException } from "@sentry/vue";
import { CollectionReference } from "firebase/firestore";
import { toValue, watch } from "vue";
// eslint-disable-next-line no-restricted-imports
import { useCollection as _useCollection, useDocument as _useDocument } from "vuefire";

export function useCollection<T>(collectionReference: Parameters<typeof _useCollection>[0]) {
  const { showErrorToast } = useToast();
  const collectionRef = _useCollection<T>(collectionReference);

  // Create the error here to capture the correct stack trace
  const error = new Error();

  // Need to catch error on the returned reactive promise for the error to become handled
  watch(
    collectionRef.promise,
    async (promise) => {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      if (!promise) return;
      try {
        await promise;
      } catch (cause) {
        const collectionReferenceValue = toValue(collectionReference);
        const collection =
          collectionReferenceValue instanceof CollectionReference
            ? collectionReferenceValue.path
            : "query";
        const query =
          collectionReferenceValue && "_query" in collectionReferenceValue
            ? collectionReferenceValue._query
            : undefined;
        error.message = `Error fetching ${collection}`;
        error.cause = cause;
        console.error(error, { query });
        captureException(error, { extra: { query } });
        showErrorToast();
      }
    },
    {
      immediate: true,
    },
  );

  return collectionRef;
}

export function useDocument<T>(documentReference: Parameters<typeof _useDocument>[0]) {
  const { showErrorToast } = useToast();
  const documentRef = _useDocument<T>(documentReference);

  // Create the error here to capture the correct stack trace
  const error = new Error();

  // Need to catch error on the returned reactive promise for the error to become handled
  watch(
    documentRef.promise,
    async (promise) => {
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      if (!promise) return;
      try {
        await promise;
      } catch (cause) {
        const documentReferenceValue = toValue(documentReference);
        error.message = `Error fetching ${documentReferenceValue?.path || "an unknown document"}`;
        error.cause = cause;
        console.error(error);
        captureException(error);
        showErrorToast();
      }
    },
    {
      immediate: true,
    },
  );

  return documentRef;
}
